// CHG-1461 / ITER-3577 — real AuthProvider implementation (closes the
// CHG-1167 stub).
//
// Hand-rolled exception per feedback_deterministic_codegen_over_hand_rolling.md:
// auth context is the layer the tier-05-experience auth-flow specs depend on,
// not driven by them. Same exception applies to LoginPage.tsx — its state
// machine ends in "navigate", which doesn't fit the auth-flow generator's
// state-renders-UI shape.
//
// Behavior:
//   - Reads "auth_token_dev" from localStorage on mount (the key v2's axios
//     interceptor at api/client.ts already injects on outgoing requests).
//   - If present → GET /users/me to hydrate Account; on 401 the token is
//     stale, clear it and treat as unauthenticated.
//   - login(email, password): POST /login → store token + Account.
//   - logout(): clear token + Account; hard-redirect to /login (provider
//     sits ABOVE BrowserRouter in main.tsx, so we can't useNavigate here).
//
// MFA: CHG-1510 / ITER-3628 (MFA program η.1) ports the mfa_required
// challenge flow. login() now returns a LoginResult discriminated
// union; LoginPage routes the user to /mfa-challenge (η.1) or
// /mfa-setup (η.2, sibling-session) based on the kind. verifyMfa +
// verifyMfaRecovery methods POST to /auth/login/mfa-verify and
// /auth/login/mfa-recovery respectively and complete the same
// store-token + hydrate-account dance as login on success.
//
// SSO: still scoped out (separate provider callback at
// /auth/sso/callback handles its own session establishment).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiClient } from "../api/client";

const TOKEN_STORAGE_KEY = "auth_token_dev";

export interface Account {
  id: string;
  email: string;
  full_name: string | null;
  // CHG-1473 / ITER-3593 — derived booleans from /users/me memberships +
  // donor_profiles. Drives Sidebar visibleWhen evaluation (admin groups
  // gated on has_staff_role; "My Giving" gated on has_donor_profile).
  // Default false during the brief window between login() and the /me
  // hydrate fetch — Sidebar treats unknown as hidden (fail-closed).
  has_staff_role: boolean;
  has_donor_profile: boolean;
  // CHG-1523 / ITER-3642 — derived from /users/me memberships where the
  // legacy `role` enum equals 'platform-admin' OR any active role
  // assignment carries role_slug='platform-admin'. Drives the Sidebar
  // Platform group visibility + future platform-only affordances.
  // Fail-closed: false during bootstrap + when no memberships carry
  // either signal. UserRole enum source of truth:
  // src/api/app/models/user.py:UserRole.platform_admin = 'platform-admin'.
  is_platform_admin: boolean;
}

/**
 * @deprecated CHG-1510 (MFA program η.1) replaced the throw-based MFA
 * branches with a typed LoginResult discriminated union (see below).
 * No code path throws this any more; the class is exported for one
 * CHG cycle so any consumer that still imports it compiles, then
 * η.2 / a follow-up CHG can delete it.
 */
export class MfaNotSupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MfaNotSupportedError";
  }
}

/**
 * CHG-1510 / ITER-3628 (MFA program η.1) + CHG-1512 / ITER-3629 (η.2) —
 * shared discriminated union for login + verifyMfa* + MFA setup outcomes.
 * Both halves of the MFA program (η.1 mfa_required + η.2
 * mfa_setup_required) use this exact shape so the cross-team merge on
 * AuthContext is mechanical. Do NOT widen, narrow, or rename a variant
 * without re-coordinating — the shape is the merge contract per
 * /tmp/mfa-program.md §Coordination.
 */
export type LoginResult =
  | { kind: "ok" }
  | { kind: "mfa_required"; mfa_token: string; mfa_methods: string[] }
  | { kind: "mfa_setup_required"; setup_token: string; reason_code: string };

export interface AuthContextValue {
  account: Account | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  /** CHG-1510 — POST /auth/login/mfa-verify with the TOTP code. On
   *  success: stores token + hydrates account + returns `{kind:'ok'}`.
   *  Backend can technically chain a second mfa_required (e.g. for
   *  recovery-code rate-limit) but we don't expect it; if it happens
   *  the caller can re-navigate. */
  verifyMfa: (mfa_token: string, code: string) => Promise<LoginResult>;
  /** CHG-1510 — POST /auth/login/mfa-recovery with a one-time
   *  recovery code. Same return semantics as verifyMfa. */
  verifyMfaRecovery: (mfa_token: string, recovery_code: string) => Promise<LoginResult>;
  logout: () => void;
  // CHG-1512 / ITER-3629 (η.2) — called by MfaSetupPage (and η.1's
  // MfaChallengePage sibling) after the MFA flow returns a full
  // LoginResponse. Centralizes token-write + /users/me hydrate so the
  // MFA pages don't duplicate AuthProvider internals.
  completeMfaLogin: (
    accessToken: string,
    accountSeed: { id: string; email: string; full_name: string | null },
  ) => Promise<void>;
}

const noopAsync = async (): Promise<LoginResult> => ({ kind: "ok" });
const noop = () => undefined;

const noopCompleteMfa = async (): Promise<void> => undefined;

const AuthContext = createContext<AuthContextValue>({
  account: null,
  isAuthenticated: false,
  isLoading: false,
  login: noopAsync,
  verifyMfa: noopAsync,
  verifyMfaRecovery: noopAsync,
  logout: noop,
  completeMfaLogin: noopCompleteMfa,
});

// CHG-1523 / ITER-3642 — typed shape for the membership entries
// is_platform_admin derivation inspects. Matches the BE
// MembershipRead schema (src/api/app/schemas/user.py): legacy `role`
// enum + active_role_assignments[] projection. Both surfaces can
// independently flag a user as platform-admin; we OR them so the
// derivation works for accounts mid-bridge from the enum-only world.
interface MembershipShape {
  role?: string | null;
  active_role_assignments?: Array<{
    role_slug?: string | null;
    is_active?: boolean;
  }>;
}

interface MeResponse {
  account: {
    id: string;
    email: string;
    full_name: string | null;
  };
  // /users/me returns memberships + donor_profiles alongside account; we
  // use their length + role data to derive the Account flags. See
  // src/api/app/routers/users.py:130 get_my_account.
  memberships?: Array<MembershipShape>;
  donor_profiles?: Array<unknown>;
}

// CHG-1523 — single source of truth for the platform-admin signal.
// Returns true when ANY membership on the authenticated account
// carries the platform-admin role on EITHER the legacy enum OR an
// active role assignment.
function derivePlatformAdmin(memberships: MembershipShape[]): boolean {
  return memberships.some((m) => {
    if (m?.role === "platform-admin") return true;
    const assignments = Array.isArray(m?.active_role_assignments)
      ? m.active_role_assignments
      : [];
    return assignments.some(
      (a) => a?.is_active === true && a?.role_slug === "platform-admin",
    );
  });
}

function buildAccount(me: MeResponse): Account {
  const memberships = Array.isArray(me.memberships) ? me.memberships : [];
  const donorProfiles = Array.isArray(me.donor_profiles) ? me.donor_profiles : [];
  return {
    id: me.account.id,
    email: me.account.email,
    full_name: me.account.full_name,
    has_staff_role: memberships.length > 0,
    has_donor_profile: donorProfiles.length > 0,
    is_platform_admin: derivePlatformAdmin(memberships),
  };
}

interface LoginApiResponse {
  access_token: string | null;
  refresh_token: string | null;
  account: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
  // CHG-1512 / ITER-3629 (η.2) — backend now surfaces both MFA branches
  // with the tokens + metadata each path needs. Carried into the
  // LoginResult discriminator below.
  mfa_required?: boolean;
  mfa_token?: string | null;
  mfa_methods?: string[] | null;
  mfa_setup_required?: boolean;
  setup_token?: string | null;
  reason_code?: string | null;
  // CHG-1512 (η.2) — BE optionally returns redirect_to (e.g. "/mfa-setup")
  // alongside mfa_setup_required. Not consumed by FE today (we hard-code
  // /mfa-setup) but surfaced here so a future iteration can route on it.
  redirect_to?: string | null;
}

function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // localStorage unavailable (private mode) — session-only auth.
  }
}

function clearStoredToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(() => readStoredToken() !== null);

  // On mount with a stored token, hydrate the account. 401 → token is
  // stale; clear it so the user is treated as logged out (ProtectedRoute
  // will then bounce them to /login).
  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient().get<MeResponse>("/users/me");
        if (cancelled) return;
        setAccount(buildAccount(r.data));
      } catch {
        if (cancelled) return;
        clearStoredToken();
        setAccount(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // CHG-1510 (η.1) — shared completion path used by login(), verifyMfa(),
  // verifyMfaRecovery() AND CHG-1512 (η.2) completeMfaLogin (below).
  // Stores the access token, hydrates account via /users/me, falls back
  // to derived-false flags on /me failure (fail-closed). Returns the
  // canonical {kind: 'ok'} LoginResult.
  const completeAuthenticatedSession = useCallback(
    async (data: LoginApiResponse): Promise<LoginResult> => {
      if (!data.access_token || !data.account) {
        throw new Error("Login response missing access_token or account.");
      }
      writeStoredToken(data.access_token);
      try {
        const me = await apiClient().get<MeResponse>("/users/me");
        setAccount(buildAccount(me.data));
      } catch {
        setAccount({
          id: data.account.id,
          email: data.account.email,
          full_name: data.account.full_name,
          has_staff_role: false,
          has_donor_profile: false,
          // CHG-1523 — fail-closed: hide the Platform group until /me
          // hydrates. Same doctrine as has_staff_role / has_donor_profile.
          is_platform_admin: false,
        });
      }
      return { kind: "ok" };
    },
    [],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const r = await apiClient().post<LoginApiResponse>("/auth/login", {
        email,
        password,
        channel: "dashboard",
      });
      // CHG-1510 (η.1) + CHG-1512 (η.2) — MFA branches return
      // discriminated LoginResult kinds instead of throwing. LoginPage
      // routes to /mfa-challenge (η.1) or /mfa-setup (η.2) based on
      // kind. Tokens + metadata surfaced verbatim from the BE response.
      if (r.data.mfa_required) {
        return {
          kind: "mfa_required",
          mfa_token: r.data.mfa_token ?? "",
          mfa_methods: Array.isArray(r.data.mfa_methods) ? r.data.mfa_methods : [],
        };
      }
      if (r.data.mfa_setup_required) {
        return {
          kind: "mfa_setup_required",
          setup_token: r.data.setup_token ?? "",
          reason_code: r.data.reason_code ?? "",
        };
      }
      return completeAuthenticatedSession(r.data);
    },
    [completeAuthenticatedSession],
  );

  const verifyMfa = useCallback(
    async (mfa_token: string, code: string): Promise<LoginResult> => {
      const r = await apiClient().post<LoginApiResponse>("/auth/login/mfa-verify", {
        mfa_token,
        code,
      });
      // Backend can theoretically re-issue mfa_required (rate-limit
      // edge) but in normal flow returns the full LoginResponse.
      if (r.data.mfa_required) {
        return {
          kind: "mfa_required",
          mfa_token: r.data.mfa_token ?? mfa_token,
          mfa_methods: Array.isArray(r.data.mfa_methods) ? r.data.mfa_methods : [],
        };
      }
      return completeAuthenticatedSession(r.data);
    },
    [completeAuthenticatedSession],
  );

  const verifyMfaRecovery = useCallback(
    async (mfa_token: string, recovery_code: string): Promise<LoginResult> => {
      const r = await apiClient().post<LoginApiResponse>(
        "/auth/login/mfa-recovery",
        { mfa_token, recovery_code },
      );
      return completeAuthenticatedSession(r.data);
    },
    [completeAuthenticatedSession],
  );

  // CHG-1512 / ITER-3629 (η.2) — exposed so MfaSetupPage (and η.1's
  // MfaChallengePage sibling) can write the access_token + hydrate the
  // Account after the user completes MFA verification, without
  // duplicating the post-login hydration logic. Same /users/me round-trip
  // + fail-closed fallback as the password-login success path above.
  // Adapts MfaSetupPage's (accessToken, accountSeed) call shape onto the
  // shared completeAuthenticatedSession helper. Same hydration path η.1's
  // verifyMfa uses; the only difference is MfaSetupPage receives the
  // verify response as separate fields rather than a full LoginApiResponse.
  // Kept as a distinct wrapper so MfaSetupPage doesn't have to construct a
  // synthetic LoginApiResponse at the call site.
  const completeMfaLogin = useCallback(
    async (
      accessToken: string,
      accountSeed: { id: string; email: string; full_name: string | null },
    ) => {
      await completeAuthenticatedSession({
        access_token: accessToken,
        refresh_token: null,
        account: accountSeed,
      });
    },
    [completeAuthenticatedSession],
  );

  const logout = useCallback(() => {
    clearStoredToken();
    setAccount(null);
    // AuthProvider sits above BrowserRouter (see main.tsx provider stack)
    // so useNavigate isn't available here — fall back to a hard redirect.
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        account,
        isAuthenticated: account !== null,
        isLoading,
        login,
        verifyMfa,
        verifyMfaRecovery,
        logout,
        completeMfaLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
